<template>
  <!-- Product Selection Modal -->
  <Teleport :to="portalRootElementSelector">
    <ModalRoot
      v-model="showProductSelectionModal"
      :aria-labelledby="selectionTitleId"
      @close="onSelectionModalClosed"
    >
      <template #body="{ close }">
        <ModalBodyV2
          class="items-center !max-w-md !px-4 sm:!px-7 !gap-6"
          @close="close"
        >
          <!-- Header -->
          <div class="flex flex-col items-center gap-2 text-center">
            <div
              class="size-14 rounded-full bg-fill-brand/10 flex items-center justify-center"
            >
              <Icon icon="mdi:shopping" class="text-3xl text-fill-brand" />
            </div>
            <h2 :id="selectionTitleId" class="font-bold text-xl text-text">
              {{ $t("channel.gating.productPurchase.selectionModal.title") }}
            </h2>
            <p class="text-text-secondary text-base">
              {{ selectionSubtitle }}
            </p>
          </div>

          <!-- Product list -->
          <div class="w-full space-y-3 self-stretch">
            <button
              v-for="product in requiredProducts"
              :key="product.id"
              :disabled="!product.product.is_active"
              class="w-full flex items-center gap-4 p-4 rounded-xl bg-surface-secondary transition-colors text-left min-h-[44px]"
              :class="[
                product.product.is_active
                  ? 'hover:bg-surface-secondary/80 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed',
              ]"
              @click="onProductSelected(product, close)"
            >
              <div
                class="size-10 rounded-full bg-fill-brand/10 flex items-center justify-center shrink-0"
              >
                <Icon
                  icon="mdi:package-variant-closed"
                  class="text-xl text-fill-brand"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <h3 class="text-text font-semibold truncate">
                    {{ product.product.name }}
                  </h3>
                  <span
                    v-if="!product.product.is_active"
                    class="text-xs bg-surface-tertiary text-text-secondary px-2 py-0.5 rounded-full whitespace-nowrap"
                  >
                    {{ $t("channel.gating.productPurchase.unavailable") }}
                  </span>
                </div>
              </div>
              <Icon
                v-if="product.product.is_active"
                icon="mdi:chevron-right"
                class="text-xl text-text-secondary shrink-0"
              />
            </button>
          </div>

          <!-- Buy All button for AND operator with multiple products -->
          <ButtonV2
            v-if="isAndOperator && activeProducts.length > 1"
            variant="primary"
            size="lg"
            class="self-stretch"
            @click="onBuyAllSelected(close)"
          >
            {{ $t("channel.gating.productPurchase.buyAll") }}
          </ButtonV2>

          <!-- Dismiss -->
          <ButtonV2
            variant="subtle"
            size="lg"
            class="self-stretch !font-sans !font-semibold"
            @click="close"
          >
            {{ $t("channel.accessModal.later") }}
          </ButtonV2>
        </ModalBodyV2>
      </template>
    </ModalRoot>
  </Teleport>

  <!-- Checkout Modal -->
  <Teleport :to="portalRootElementSelector">
    <ModalRoot v-model="showCheckoutModal" @close="onCheckoutClosed">
      <template #body="{ close }">
        <ProductCheckout
          v-if="selectedProductId"
          :product-id="selectedProductId"
          :title="$t('channel.gating.productPurchase.checkoutModal.title')"
          @close="close"
          @success="onCheckoutSuccess"
        />
      </template>
    </ModalRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { Icon } from "@iconify/vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ModalBodyV2 from "@/components/ui/modal/modal-body-v2.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import ProductCheckout from "@/components/checkout/product-checkout.vue";
import { portalRootElementSelector } from "@/constants/ui";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user.store";
import type { AuthorizationRuleForGating } from "@/features/gating/use-gating-permissions";

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStore();

const emit = defineEmits<{
  "product-purchased": [];
  "upgrade-cancelled": [];
}>();

// Internal state
const showProductSelectionModal = ref(false);
const showCheckoutModal = ref(false);
const selectedProductId = ref<string | null>(null);
const requestedRule = ref<AuthorizationRuleForGating | null>(null);

// Register with the just-one-open-modal system
useJustOneOpenModal(showProductSelectionModal);
useJustOneOpenModal(showCheckoutModal);

// Unique ID for aria-labelledby
const selectionTitleId = "product-selection-modal-title";

// Derived data from the requested rule
const criterion = computed(
  () => requestedRule.value?.product_purchase_criterion ?? null,
);

const requiredProducts = computed(
  () => criterion.value?.required_products ?? [],
);

const activeProducts = computed(() =>
  requiredProducts.value.filter((p) => p.product.is_active),
);

const isAndOperator = computed(() => criterion.value?.operator === "AND");

const selectionSubtitle = computed(() => {
  if (isAndOperator.value) {
    return t("channel.gating.productPurchase.selectionModal.subtitleAll");
  }
  return t("channel.gating.productPurchase.selectionModal.subtitleAny");
});

// Public methods (exposed to parent via template ref)
function showUpgradeFlow(rule?: AuthorizationRuleForGating | null) {
  requestedRule.value = rule ?? null;

  const products = rule?.product_purchase_criterion?.required_products ?? [];
  const active = products.filter((p) => p.product.is_active);

  // Track denial event
  userStore.trackAnalyticsAction({
    appActionName: "GATING_DENIED_PRODUCT",
    actionMetadata: {
      productCount: products.length,
      operator: rule?.product_purchase_criterion?.operator ?? "unknown",
    },
  });

  // Single active product: skip directly to checkout
  if (active.length === 1 && active[0]) {
    selectedProductId.value = active[0].id;
    showCheckoutModal.value = true;

    userStore.trackAnalyticsAction({
      appActionName: "GATING_PRODUCT_PURCHASE_INITIATED",
      objectId: active[0].id,
      actionMetadata: {
        productName: active[0].product.name,
        skippedSelection: true,
      },
    });
    return;
  }

  // Multiple products or no active products: show selection modal
  showProductSelectionModal.value = true;
}

// Event handlers
function onProductSelected(
  product: { id: string; product: { name: string; is_active: boolean } },
  closeModal: () => void,
) {
  if (!product.product.is_active) return;

  closeModal();
  selectedProductId.value = product.id;
  showCheckoutModal.value = true;

  userStore.trackAnalyticsAction({
    appActionName: "GATING_PRODUCT_PURCHASE_INITIATED",
    objectId: product.id,
    actionMetadata: {
      productName: product.product.name,
      skippedSelection: false,
    },
  });
}

function onBuyAllSelected(closeModal: () => void) {
  // For AND operator "Buy All": pick the first active product to start checkout.
  // After purchase, the user will need to return for the remaining products.
  const firstActive = activeProducts.value[0];
  if (!firstActive) return;

  closeModal();
  selectedProductId.value = firstActive.id;
  showCheckoutModal.value = true;

  userStore.trackAnalyticsAction({
    appActionName: "GATING_PRODUCT_PURCHASE_INITIATED",
    objectId: firstActive.id,
    actionMetadata: {
      productName: firstActive.product.name,
      buyAll: true,
      totalProducts: activeProducts.value.length,
    },
  });
}

function onSelectionModalClosed() {
  showProductSelectionModal.value = false;
  requestedRule.value = null;
  emit("upgrade-cancelled");
}

function onCheckoutClosed() {
  showCheckoutModal.value = false;
  selectedProductId.value = null;
  emit("upgrade-cancelled");
}

function onCheckoutSuccess(paymentIntentId: string) {
  showCheckoutModal.value = false;
  selectedProductId.value = null;

  userStore.trackAnalyticsAction({
    appActionName: "GATING_PRODUCT_PURCHASE_COMPLETED",
    actionMetadata: {
      paymentIntentId,
    },
  });

  // Redirect with payment_intent so PurchaseCompletionHandler can process it
  router.replace({
    query: {
      payment_intent: paymentIntentId,
    },
  });

  emit("product-purchased");
}

// Expose methods to parent
defineExpose({
  showUpgradeFlow,
});
</script>
